import { useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'store';
import {
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
  GridToolbar,
  GridInitialState,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Box, 
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import { ResponsiveDataGrid } from 'ui-component/ResponsiveDataGrid';
import ConfirmationDialog from 'ui-component/ConfirmationDialog';
import { useTheme } from '@mui/material/styles';
import { IUser } from './../../../../../types/user.interface';
import { useUsers } from '../../../../../hooks/useUsers.hooks';
import { useCurrentOrganization } from './../../../../../hooks/useOrganization.hooks'
import { useUser } from '../../../../../hooks/useUser.hooks';
import { useCurrentUser } from '../../../../../hooks/useUser.hooks';
import { openSnackbar } from 'store/slices/snackbar';
import TableButton from 'ui-component/TableButton';

// export default function UsersDataGrid({setUser} : {setUser: React.Dispatch<React.SetStateAction<IUser>>}) {
export default function UsersDataGrid(props: any) {
  const { role, active } = props;
  const theme = useTheme();
  const usersState = useUsers(role, active);
  const userState = useUser();
  const organizationState = useCurrentOrganization();
  const currentUserState = useCurrentUser();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);
  const [showReactivateConfirmation, setShowReactivateConfirmation] = useState(false);
  const [activeUserID, setActiveUserID] = useState(-1);
  const [resendInProgressId, setResendInProgressId] = useState<number | null>(null);

  const apiRef = useGridApiRef();
  const [initialState, setInitialState] = useState<GridInitialState>();

  const saveSnapshot = useCallback(() => {
    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();
      localStorage.setItem('adminAdminsUserDataGridState', JSON.stringify(currentState));
    }
  }, [apiRef]);

  useLayoutEffect(() => {
    const stateFromLocalStorage = localStorage?.getItem('adminAdminsUserDataGridState');
    setInitialState(stateFromLocalStorage ? JSON.parse(stateFromLocalStorage) : {});

    // handle refresh and navigating away/refreshing
    window.addEventListener('beforeunload', saveSnapshot);

    return () => {
      // in case of an SPA remove the event-listener
      window.removeEventListener('beforeunload', saveSnapshot);
      saveSnapshot();
    };
  }, [saveSnapshot]);

  const formatDateTime = (value?: string | null) => {
    if (!value) {
      return 'Never';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'Never' : date.toLocaleString();
  };

  const handleResendSetupEmail = async (targetUser: IUser) => {
    if (!userState.resendSetupEmail) {
      return;
    }

    setResendInProgressId(targetUser.id);
    try {
      const data = await userState.resendSetupEmail(targetUser.email);
      dispatch(
        openSnackbar({
          open: true,
          message: data?.message || 'Verification email resent successfully.',
          variant: 'alert',
          alert: { color: 'success' },
          close: false,
        }),
      );
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Unable to send verification email.';
      dispatch(
        openSnackbar({
          open: true,
          message,
          variant: 'alert',
          alert: { color: 'error' },
          close: false,
        }),
      );
    } finally {
      setResendInProgressId(null);
    }
  };

  const columns: GridColDef[] = [
    ...(!active ? [{
      field: 'time_remaining', 
      headerName: 'Remaining time until permanently deleted', 
      width: 150,
      sortable: true,
      type: 'number' as const,
      renderCell: (params: { row: any; }) => (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <Chip 
        style={{ backgroundColor: theme.palette.error.light, alignItems: 'center' }}
        label={params.row.time_remaining + " days"}
      />
      </Box>
      )
    }] : []),
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: (params: GridRowParams) => {
        if (active) {
          const isCurrentUser = currentUserState.ready && currentUserState.user.id === params.row.id;
          return [
            <GridActionsCellItem
              icon={
                <Tooltip title="Edit">
                  <EditIcon />
                </Tooltip>
              }
              label="Edit"
              className="textPrimary"
              onClick={() => navigate('/administrator/update/' + params.row.id)}
              color="inherit"
            />,
            <Tooltip
              title={isCurrentUser ? "You cannot archive yourself" : ""}
              arrow
            >
              <span>
                <GridActionsCellItem
                  icon={
                    <Tooltip title="Archive">
                      <ArchiveOutlinedIcon />
                    </Tooltip>
                  }
                  label="Archive"
                  onClick={() => {
                    setActiveUserID(params.row.id as number);
                    setShowArchiveConfirmation(true);
                  }}
                  disabled={isCurrentUser}
                  color="inherit"
                />
              </span>
            </Tooltip>,
          ];
        }
        return [
          <GridActionsCellItem
            icon={
              <Tooltip title="Reactivate">
                <UnarchiveOutlinedIcon />
              </Tooltip>
            }
            label="Reactivate"
            onClick={() => {
              setActiveUserID(params.row.id as number);
              setShowReactivateConfirmation(true);
            }}
            color="inherit"
          />,
        ];
      },
    },
    { field: 'region_name', headerName: 'Region', width: 100,
    valueGetter: (value, row) =>
      {
        return `${row.region.name}`},
    },
    { field: 'first_name', headerName: 'First Name', width: 130 },
    { field: 'last_name', headerName: 'Last name', width: 130 },
    { field: 'email', headerName: 'Email', width: 300 },
    { field: 'password_set', headerName: 'Password Set', width: 140,
      valueGetter: (value, row) => (row?.must_setup_password ? 'No' : 'Yes')
    },
    { field: 'email_verified', headerName: 'Email Verified', width: 140,
      valueGetter: (value, row) => ((row?.email_verified || row?.email_verified_at) ? 'Yes' : 'No')
    },
    { field: 'last_setup_email_sent', headerName: 'Last Setup Email', width: 190,
      valueGetter: (value, row) => formatDateTime(row?.last_setup_email_sent)
    },
    {
      field: 'resend_setup_email',
      headerName: 'Resend Verification',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params: { row: IUser }) => {
        const handleClick = (event: any) => {
          event.stopPropagation();
          handleResendSetupEmail(params.row);
        };

        const isProcessing = resendInProgressId === params.row.id;
        return (
          <TableButton
            onClick={handleClick}
            disabled={!userState.resendSetupEmail || isProcessing}
          >
            {isProcessing ? 'Sending...' : 'Resend Verification'}
          </TableButton>
        );
      },
    },
    {
      field: 'id',
      headerName: 'ID',
      type: 'string',
      width: 100,
      editable: false,
    },
  ];

  const [rows, setRows] = useState<IUser[]>([]);
  let usersArray : IUser[];
  useEffect(() => {
    if (usersState?.ready && organizationState.ready && !userState.deleteInProgress) {
      usersArray = Object.values(usersState.users).sort((a : any, b : any) => a.first_name.localeCompare(b.first_name));
      setRows(usersArray);
    }
  }, [usersState?.ready, organizationState.ready, userState?.deleteInProgress, usersState.users]);
  
  const onRowsSelectionHandler = (ids: any) => {
    // const selectedRowsData = ids.map((id: any) => rows.find((row) => row.id === id));
    // setUser(selectedRowsData[0]);
  };

  const cancelDialog = () => {
    setShowArchiveConfirmation(false);
    setShowReactivateConfirmation(false);
  }

  const confirmArchive = async () => {
    await userState.deleteUser(activeUserID);
    await usersState.fetch(); // to refresh users
    setShowArchiveConfirmation(false);
  };

  const confirmReactivate = async () => {
    await userState.reactivateUser(activeUserID);
    await usersState.fetch(); // to refresh users
    setShowReactivateConfirmation(false);
  };

  if (!usersState?.ready) {
    return (
      <div>Loading...</div>
  )
  }

  // Handle saved table views
  if (!initialState) {
    return <CircularProgress />;
  }
  
  return (
    <div style={{ height: 700, width: '100%' }}>
      <Box
        sx={{
        height: 700,
        width: '100%',
        '& .actions': {
          color: 'text.secondary',
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
        '& .MuiDataGrid-root': {
            border: 'none',
            '& .MuiDataGrid-cell': {
                borderColor: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
            },
            '& .MuiDataGrid-columnsContainer': {
                color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900',
                borderColor: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
            },
            '& .MuiDataGrid-columnSeparator': {
                color: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
            }
          },
          '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
            borderRight: `1px solid ${
              theme.palette.mode === 'light' ? '#f0f0f0' : '#303030'
            }`,
          },
      }}
      >
      <ResponsiveDataGrid
      rows={rows} 
      columns={columns}
      pagination
      slots={{ toolbar: GridToolbar }}
      onRowSelectionModelChange={(ids : any) => onRowsSelectionHandler(ids)}
      apiRef={apiRef}
      initialState={{
        ...initialState,
        sorting: {
          sortModel: [{ field: 'time_remaining', sort: 'asc' }],
        },
      }}
      />
      <ConfirmationDialog
          open={showArchiveConfirmation}
          onCancel={cancelDialog}
          onConfirm={confirmArchive}
          title="Archive User?"
          message="Confirm you want to archive this user?"
      />
      <ConfirmationDialog
          open={showReactivateConfirmation}
          onCancel={cancelDialog}
          onConfirm={confirmReactivate}
          title="Reactivate User?"
          message="Confirm you want to reactivate this user?"
      />
      </Box>
    </div>
  );
}
